import React, { useState, useMemo } from 'react';
import Table from '../../components/Table/Table';
import CategoryForm from '../../components/Form/CategoryForm';
import { Category, TableColumn } from '../../types';
import { exportToExcel, printAsExcel } from '../../utils/exportToExcel';
import { useCategories, useCreateCategory, useUpdateCategory, useDeleteCategory } from '../../hooks/useCategories';
import TableSkeleton from '../../components/Loading/TableSkeleton';

const Categories: React.FC = () => {
  // Query options for fetching categories
  const queryOptions = useMemo(() => ({
    pagination: { page: 1, pageSize: 1000 },
    sort: [{ field: 'name', order: 'asc' as const }]
  }), []);

  // Fetch categories using the API hook
  const { data: categories, isLoading, error, refetch } = useCategories(queryOptions);

  // Create, update, and delete mutations
  const createMutation = useCreateCategory(() => {
    refetch();
  });

  const updateMutation = useUpdateCategory(() => {
    refetch();
  });

  const deleteMutation = useDeleteCategory(() => {
    refetch();
  });

  const [showForm, setShowForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | undefined>();

  // Helper to get parent category name
  // const getParentName = (parentId: string | null) => {
  //   if (!parentId) return '-';
  //   const parent = categories.find(cat => cat.id === parentId);
  //   return parent ? parent.name : '-';
  // };

  const columns: TableColumn[] = [
    { key: 'name', label: 'Name', sortable: true },
    { key: 'type', label: 'Type', sortable: true, render: (value: string) => (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
        value === 'income' ? 'bg-green-100 text-green-800' : 
        value === 'expense' ? 'bg-red-100 text-red-800' : 
        'bg-blue-100 text-blue-800'
      }`}>
        {value ? value.charAt(0).toUpperCase() + value.slice(1) : 'Unknown'}
      </span>
    ) },
    { key: 'description', label: 'Description', sortable: false },
    { key: 'isActive', label: 'Status', sortable: true, render: (value: boolean) => (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${value ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>{value ? 'Active' : 'Inactive'}</span>
    ) },
    { key: 'createdAt', label: 'Created', sortable: true, render: (value: string) => new Date(value).toLocaleDateString() },
  ];

  const handleEdit = (category: Category) => {
    setEditingCategory(category);
    setShowForm(true);
  };

  const handleDelete = async (category: Category) => {
    if (window.confirm(`Are you sure you want to delete ${category.name}?`)) {
      await deleteMutation.execute(category);
    }
  };

  const handleAdd = () => {
    setEditingCategory(undefined);
    setShowForm(true);
  };

  const handleFormSubmit = async (data: Partial<Category>) => {
    if (editingCategory) {
      // Update existing category - include documentId in data
      const updateData = {
        ...data,
        documentId: editingCategory.documentId
      };
      await updateMutation.execute({ id: editingCategory.id, data: updateData });
    } else {
      // Add new category
      await createMutation.execute(data);
    }
    setShowForm(false);
    setEditingCategory(undefined);
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingCategory(undefined);
  };

  const handleExport = () => {
    if (!categories) return;
    const exportData = categories.map(category => ({
      'Category Name': category.name,
      'Type': category.type,
      'Description': category.description,
      'Status': category.isActive ? 'Active' : 'Inactive',
      'Created': new Date(category.createdAt).toLocaleDateString()
    }));
    
    exportToExcel(exportData, `categories_${new Date().toISOString().split('T')[0]}`);
  };

  const handlePrint = () => {
    if (!categories) return;
    const exportData = categories.map(category => ({
      'Category Name': category.name,
      'Type': category.type,
      'Description': category.description,
      'Status': category.isActive ? 'Active' : 'Inactive',
      'Created': new Date(category.createdAt).toLocaleDateString()
    }));
    
    printAsExcel(exportData, 'Categories');
  };

  if (showForm) {
    return (
      <div className="space-y-6">
        <CategoryForm
          category={editingCategory}
          onSubmit={handleFormSubmit}
          onCancel={handleFormCancel}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Categories</h1>
          <p className="text-gray-600">Manage your income and expense categories</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={handleExport}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors"
          >
            Export
          </button>
          <button
            onClick={handlePrint}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
          >
            Print
          </button>
        </div>
      </div>
      
      {isLoading ? (
        <TableSkeleton />
      ) : error ? (
        <div className="card p-6 text-center text-red-600">
          Error loading categories: {error.message || String(error)}
        </div>
      ) : (
      <Table
        columns={columns}
          data={categories || []}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onAdd={handleAdd}
        title="Categories"
        searchPlaceholder="Search categories..."
      />
      )}
    </div>
  );
};

export default Categories; 